import React, { useState, useCallback, useRef } from "react";
import { Flex, Text, Button, TextField, Popover, IconButton, Tooltip, Checkbox } from "@radix-ui/themes";
import { Cross1Icon, UploadIcon } from "@radix-ui/react-icons";

const validateFileSize = (file) => {
  const maxSizeInBytes = 1048576; // 1MB
  return file && file.size <= maxSizeInBytes;
};

const LinkEditor = ({
  children,
  links,
  setLinks,
  canHaveUniqueUrls,
  brandLocations,
  setToastMessage,
  setIsDirty,
  editingLink = null,
  editingIndex = null,
  isAddingLink = false,
  onLinksChange, // New prop to handle links changes
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentEditingLink, setCurrentEditingLink] = useState(null);
  const [locationLinkUrls, setLocationLinkUrls] = useState({});
  
  // Icon upload state - similar to other image uploads in GuestPortal
  const [iconUpload, setIconUpload] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [iconFileName, setIconFileName] = useState("");
  const [deleteIcon, setDeleteIcon] = useState(false); // Track icon deletion
  const iconInputRef = useRef(null);

  // Handle trigger click
  const handleTriggerClick = useCallback(() => {
    setIsOpen(true);

    if (editingLink) {
      console.log("editingLink:", editingLink);
      // Editing existing link
      setCurrentEditingLink({ ...editingLink, index: editingIndex });

      // Set icon preview if exists
      if (editingLink.icon || editingLink.icon_preview) {
        setIconPreview(editingLink.icon || editingLink.icon_preview);
        setIconFileName(editingLink.icon_filename || "");
        setDeleteIcon(false); // Reset deletion flag when editing existing link
      } else {
        setIconPreview(null);
        setIconFileName("");
        setDeleteIcon(false);
      }

      // Initialize location URLs if unique URLs are enabled
      if (editingLink.unique_url_flag && canHaveUniqueUrls) {
        setLocationLinkUrls({
          [editingLink.id]: editingLink.location_link_urls || {},
        });
      }
    } else if (isAddingLink) {
      // Adding new link
      const tempId = `temp_${Date.now()}`;
      setCurrentEditingLink({
        id: tempId,
        label: "",
        link_url: "",
        icon: null,
        icon_filename: null,
        unique_url_flag: false,
        color: "#000000",
        text_color: "#FFFFFF",
      });
      setIconPreview(null);
      setIconFileName("");
      setIconUpload(null);
      setDeleteIcon(false);
      setLocationLinkUrls({});
    }
  }, [editingLink, editingIndex, isAddingLink, canHaveUniqueUrls]);

  // Handle icon upload - similar to handleLogoUpload in GuestPortal
  const handleIconUpload = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      if (!validateFileSize(file)) {
        setToastMessage("Maximum file size of 1MB");
        return;
      }

      setIconUpload(file);
      setIconFileName(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
        setToastMessage("Uploaded");
        setDeleteIcon(false); // Reset deletion flag when uploading new icon
        setIsDirty(true);
      };
      reader.readAsDataURL(file);
    },
    [setToastMessage, setIsDirty]
  );

  // Handle icon clear - similar to handleLogoClear in GuestPortal
  const handleIconClear = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent the click from bubbling up to the label
    
    // Check if there was an existing icon (not just a preview from upload)
    const hadExistingIcon = currentEditingLink?.icon && !iconUpload;
    
    setIconUpload(null);
    setIconPreview(null);
    setIconFileName("");
    if (iconInputRef.current) {
      iconInputRef.current.value = "";
    }
    
    // Set deletion flag only if there was an existing icon
    setDeleteIcon(hadExistingIcon);
    setIsDirty(true);
  }, [setIsDirty, currentEditingLink, iconUpload]);

  // Trigger icon input
  const triggerIconInput = useCallback(() => {
    if (iconInputRef.current) {
      iconInputRef.current.click();
    }
  }, []);

  // Handle location URL changes
  const handleLocationUrlChange = useCallback(
    (locationId, value) => {
      setLocationLinkUrls((prev) => ({
        ...prev,
        [currentEditingLink.id]: {
          ...prev[currentEditingLink.id],
          [locationId]: value,
        },
      }));
      setIsDirty(true);
    },
    [currentEditingLink?.id, setIsDirty]
  );

  // Update link field
  const updateLinkField = useCallback(
    (field, value) => {
      setCurrentEditingLink((prev) => ({
        ...prev,
        [field]: value,
      }));
      setIsDirty(true);
    },
    [setIsDirty]
  );

  function getButtonLinkTextColorBasedOnBackground(color) {
    color = color.replace("#", "");
    // Converting the hex color to RGB
    const r = parseInt(color.substring(0, 2), 16) / 255;
    const g = parseInt(color.substring(2, 4), 16) / 255;
    const b = parseInt(color.substring(4, 6), 16) / 255;

    // Calculating luminance using the formula
    const luminance =
      0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)) +
      0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)) +
      0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4));

    // If luminance is higher than 0.5, using black text; otherwise, white text
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  // Check if there's meaningful content to save
  const hasContent = useCallback(() => {
    return (
      (currentEditingLink?.label && currentEditingLink.label.trim() !== "") ||
      (currentEditingLink?.link_url && currentEditingLink.link_url.trim() !== "") ||
      (currentEditingLink?.color && currentEditingLink.color.trim() !== "") ||
      iconPreview
    );
  }, [currentEditingLink, iconPreview]);

  // Save link changes (local state only - no API calls)
  const saveLinkChanges = useCallback(() => {
    if (!currentEditingLink || !hasContent()) return;
    
    const updatedLink = {
      ...currentEditingLink,
      icon_file: iconUpload, // This is the File object for backend
      iconUpload: iconUpload, // Keep both for compatibility
      icon_preview: iconPreview,
      icon_filename: iconFileName,
      icon: iconPreview, // Also set icon for backward compatibility
      delete_icon: deleteIcon, // Include deletion flag
    };

    // Add location URLs if unique URLs are enabled
    if (currentEditingLink.unique_url_flag && canHaveUniqueUrls) {
      updatedLink.location_link_urls = locationLinkUrls[currentEditingLink.id] || {};
    }

    let updatedLinks;
    if (isAddingLink) {
      // Add new link
      updatedLinks = [...links, updatedLink];
    } else {
      // Update existing link
      updatedLinks = [...links];
      updatedLinks[currentEditingLink.index] = updatedLink;
    }

    setLinks(updatedLinks);
    if (onLinksChange) {
      onLinksChange(updatedLinks);
    }
  }, [
    currentEditingLink,
    hasContent,
    isAddingLink,
    iconUpload,
    iconPreview,
    iconFileName,
    deleteIcon,
    canHaveUniqueUrls,
    locationLinkUrls,
    links,
    setLinks,
    onLinksChange,
  ]);

  // Remove link (local state only - no API calls)
  const removeLink = useCallback(() => {
    if (!currentEditingLink) return;

    const updatedLinks = links.filter((_, i) => i !== currentEditingLink.index);
    setLinks(updatedLinks);
    if (onLinksChange) {
      onLinksChange(updatedLinks);
    }
    setIsOpen(false);
  }, [currentEditingLink, links, setLinks, onLinksChange]);

  // Handle popover close
  const handlePopoverClose = useCallback(
    (open) => {
      if (!open) {
        // Save changes when closing if there's content
        if (hasContent()) {
          saveLinkChanges();
        }
        setIsOpen(false);
      }
    },
    [hasContent, saveLinkChanges]
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={handlePopoverClose}>
      <Popover.Trigger asChild onClick={handleTriggerClick}>
        {children}
      </Popover.Trigger>
      <Popover.Content side="left">
        <Flex direction="column" gap="4">
          <Flex style={{ alignSelf: "stretch", justifyContent: "space-between", alignItems: "center" }}>
            <Text size="3" weight="medium">Link settings</Text>
            <Popover.Close>
              <IconButton size="1" variant="ghost" color="gray">
                <Cross1Icon />
              </IconButton>
            </Popover.Close>
          </Flex>

          {/* Link form fields */}
          <Flex direction="column" gap="4">
            {/* Label field */}
            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
              <Flex gap="1" direction="column">
                <Text size="2" weight="medium">
                  Label
                </Text>
                <TextField.Root
                  size="2"
                  value={currentEditingLink?.label || ""}
                  onChange={(e) => updateLinkField("label", e.target.value)}
                  placeholder="Enter link label"
                />
              </Flex>
            </Text>

            {/* Icon field */}
            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
              <Flex gap="1" direction="column">
                <Text size="2" weight="medium">
                  Icon
                </Text>
                <Flex gap="2">
                  <input
                    type="file"
                    style={{ display: "none" }}
                    ref={iconInputRef}
                    onChange={handleIconUpload}
                    accept="image/*"
                  />
                  <Button
                    type="button"
                    size="2"
                    variant="surface"
                    color="gray"
                    style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                    onClick={triggerIconInput}
                  >
                    {iconPreview ? (
                      <Tooltip content="Upload new to replace">
                        <Text>
                          {iconFileName?.length >= 18
                            ? `${iconFileName.slice(0, 18)}...`
                            : iconFileName || "Icon uploaded"}
                        </Text>
                      </Tooltip>
                    ) : (
                      <>
                        <UploadIcon />
                        <Text>Upload</Text>
                      </>
                    )}
                  </Button>
                  {iconPreview && (
                    <Button type="button" size="2" variant="surface" color="gray" onClick={handleIconClear}>
                      Clear
                    </Button>
                  )}
                </Flex>
                <Text size="1" weight="regular" color="gray">
                  Recommended dimensions: 40 x 40px. Maximum file size: 1MB.
                </Text>
              </Flex>
            </Text>

            {/* Link URL field */}
            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
              <Flex gap="2" direction="column">
                <Flex gap="1" direction="column">
                  <Text size="2" weight="medium">
                    Link URL
                  </Text>
                  {!(currentEditingLink?.unique_url_flag && canHaveUniqueUrls) && (
                    <TextField.Root
                      size="2"
                      value={currentEditingLink?.link_url || ""}
                      onChange={(e) => updateLinkField("link_url", e.target.value)}
                      placeholder="Enter URL"
                    />
                  )}
                </Flex>
                {canHaveUniqueUrls && (
                  <Text as="label" size="1">
                    <Flex gap="2">
                      <Checkbox
                        checked={currentEditingLink?.unique_url_flag}
                        onCheckedChange={(val) => updateLinkField("unique_url_flag", val)}
                      />
                      Unique URL per location
                    </Flex>
                  </Text>
                )}
              </Flex>
            </Text>

            {/* Location-specific URLs */}
            {currentEditingLink?.unique_url_flag && canHaveUniqueUrls && (
              <Flex
                p="2"
                direction="column"
                gap="2"
                style={{
                  maxHeight: "120px",
                  alignItems: "flex-start",
                  alignSelf: "stretch",
                  overflow: "auto",
                  border: "1px solid var(--gray-a6)",
                  borderRadius: "var(--radius-2)",
                  backgroundColor: "var(--color-panel-translucent)",
                }}
              >
                {brandLocations.map((loc) => (
                  <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }} key={loc.id}>
                    <Text size="2" weight="medium">
                      {loc.name}
                    </Text>
                    <TextField.Root
                      size="2"
                      value={locationLinkUrls[currentEditingLink.id]?.[loc.id] || ""}
                      onChange={(e) => handleLocationUrlChange(loc.id, e.target.value)}
                      placeholder="Enter URL for this location"
                    />
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
          
          {/* Color picker */}
          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
            <Flex gap="1" direction="column">
              <Text size="2" weight="medium">
                Background colour
              </Text>

              <Flex gap="2" style={{ alignItems: "center", width: "100%" }}>
                <label
                  htmlFor="link_color_picker"
                  style={{
                    position: "relative",
                    display: "block",
                    width: "100%",
                  }}
                >
                  <input
                    type="color"
                    id="link_color_picker"
                    value={currentEditingLink?.color || "#000000"}
                    onChange={(e) => {
                      updateLinkField("color", e.target.value);
                      const textColor = getButtonLinkTextColorBasedOnBackground(e.target.value);
                      updateLinkField("text_color", textColor);
                    }}
                    style={{
                      opacity: 0,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                      padding: 0,
                      margin: 0,
                      cursor: "pointer",
                    }}
                    aria-label="Link color picker"
                  />
                  <div
                    style={{
                      width: "100%",
                      height: "32px",
                      backgroundColor: currentEditingLink?.color || "#000000",
                      borderRadius: "var(--radius-2)",
                      border: "1px solid var(--gray-a6)",
                    }}
                  />
                </label>
              </Flex>
            </Flex>
          </Text>
          {/* Remove button - only show for existing links */}
          {!isAddingLink && (
            <Flex style={{ alignSelf: "stretch", marginTop: "var(--space-2)" }}>
              <Button type="button" size="2" variant="soft" color="gray" onClick={removeLink} style={{ width: "100%" }}>
                Remove link
              </Button>
            </Flex>
          )}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};

export default LinkEditor;
